import React, { useEffect } from "react";
import {
  FaCloud,
  FaLock,
  FaUsers,
  FaChartLine,
  FaCheckCircle,
  FaGlobeAsia,
  FaShieldAlt,
  FaTools,
  FaHandshake,
} from "react-icons/fa";
import {
  SiGmail,
  SiGooglemeet,
  SiGooglecalendar,
  SiGoogledocs,
  SiGoogledrive,
  SiGooglesheets,
  SiGoogleslides,
} from "react-icons/si";

import AOS from "aos";
import "aos/dist/aos.css";
import gmailIcon    from '../../images/google/Google-Workspace-logos/Gmail/logo_gmail_2026q2_color_2x_web_48dp.png';
import meetIcon     from '../../images/google/Google-Workspace-logos/Meet/logo_meet_2026q2_color_2x_web_48dp.png';
import calendarIcon from '../../images/google/Google-Workspace-logos/Calendar/logo_calendar_2026q2_color_2x_web_48dp.png';
import docsIcon     from '../../images/google/Google-Workspace-logos/Docs/logo_docs_2026q2_color_2x_web_48dp.png';
import sheetsIcon   from '../../images/google/Google-Workspace-logos/Sheets/logo_sheets_2026q2_color_2x_web_48dp.png';
import slidesIcon   from '../../images/google/Google-Workspace-logos/Slides/logo_slides_2026q2_color_2x_web_48dp.png';
import driveIcon    from '../../images/google/Google-Workspace-logos/Drive/logo_drive_2026q2_color_2x_web_48dp.png';
import chatIcon     from '../../images/google/Google-Workspace-logos/Chat/logo_chat_2026q2_color_2x_web_48dp.png';

const Overview = () => {
  useEffect(() => {
    // AOS 애니메이션 라이브러리 초기화
    AOS.init({
      duration: 800,
      easing: "ease-out",
      once: true,
    });
  }, []);

  // Google Workspace 핵심 가치
  const coreValues = [
    {
      icon: <FaCloud />,
      title: "클라우드 네이티브",
      description: "언제 어디서나 접근 가능한 완전한 클라우드 솔루션",
    },
    {
      icon: <FaUsers />,
      title: "실시간 협업",
      description: "팀원들과 동시에 작업하며 아이디어 공유",
    },
    {
      icon: <FaLock />,
      title: "엔터프라이즈 보안",
      description: "데이터를 안전하게 보호하는 고급 보안 기능",
    },
    {
      icon: <FaChartLine />,
      title: "비즈니스 인텔리전스",
      description: "데이터 기반 의사결정을 위한 분석 도구",
    },
  ];

  // Google Workspace 주요 앱
  const apps = [
    {
      icon: <img src={gmailIcon} alt="Gmail" className="w-5 h-5" />,
      title: "Gmail",
      description: "스마트한 기업용 이메일",
      features: [
        "맞춤형 비즈니스 이메일 주소",
        "스팸 및 악성코드 보호",
        "통합 검색 및 필터링",
      ],
      color: "red",
    },
    {
      icon: <img src={meetIcon} alt="Meet" className="w-5 h-5" />,
      title: "Meet",
      description: "고품질 화상 회의",
      features: [
        "최대 250명 동시 참여",
        "화면 공유 및 실시간 자막",
        "회의 녹화 및 저장",
      ],
      color: "blue",
    },
    {
      icon: <img src={driveIcon} alt="Drive" className="w-5 h-5" />,
      title: "Drive",
      description: "안전한 클라우드 스토리지",
      features: [
        "무제한 클라우드 저장공간",
        "고급 공유 권한 설정",
        "강력한 파일 검색 기능",
      ],
      color: "green",
    },
    {
      icon: <img src={docsIcon} alt="Docs" className="w-5 h-5" />,
      title: "Docs",
      description: "스마트한 문서 작성",
      features: [
        "실시간 공동 편집",
        "자동 저장 및 버전 관리",
        "오프라인 작업 지원",
      ],
      color: "blue",
    },
    {
      icon: <img src={sheetsIcon} alt="Sheets" className="w-5 h-5" />,
      title: "Sheets",
      description: "강력한 스프레드시트",
      features: [
        "고급 데이터 분석 도구",
        "커스텀 매크로 및 스크립트",
        "외부 데이터 통합",
      ],
      color: "green",
    },
    {
      icon: <img src={slidesIcon} alt="Slides" className="w-5 h-5" />,
      title: "Slides",
      description: "인상적인 프레젠테이션",
      features: [
        "전문적인 템플릿 제공",
        "실시간 협업 편집",
        "다양한 미디어 지원",
      ],
      color: "yellow",
    },
    {
      icon: <img src={calendarIcon} alt="Calendar" className="w-5 h-5" />,
      title: "Calendar",
      description: "스마트한 일정 관리",
      features: ["회의실 리소스 예약", "다중 캘린더 관리", "외부 공유 및 초대"],
      color: "blue",
    },
    {
      icon: <img src={chatIcon} alt="Chat" className="w-5 h-5" />,
      title: "Chat",
      description: "팀 커뮤니케이션 허브",
      features: [
        "직접 메시징 및 그룹 채팅",
        "Google 앱 통합",
        "스마트 봇 및 자동화",
      ],
      color: "green",
    },
  ];

  // 요금제 정보
  const plans = [
    {
      name: "Business Starter",
      price: "$7",
      priceDesc: "사용자/월",
      storage: "30GB",
      meet: "100명",
      security: "기본",
      support: "표준",
      features: [
        "커스텀 비즈니스 이메일",
        "화상 회의",
        "공유 캘린더",
        "문서, 스프레드시트, 프레젠테이션",
        "보안 관리",
        "표준 지원",
      ],
    },
    {
      name: "Business Standard",
      price: "$14",
      priceDesc: "사용자/월",
      storage: "2TB",
      meet: "150명",
      security: "고급",
      support: "표준",
      isPopular: true,
      features: [
        "Starter의 모든 기능",
        "화상 회의 녹화 기능",
        "참석자 추적",
        "노이즈 캔슬링",
        "설문 및 Q&A",
        "공유 드라이브",
      ],
    },
    {
      name: "Business Plus",
      price: "$22",
      priceDesc: "사용자/월",
      storage: "5TB",
      meet: "250명",
      security: "고급+",
      support: "향상됨",
      features: [
        "Standard의 모든 기능",
        "향상된 보안 및 관리",
        "이메일 인사이트",
        "데이터 유출 방지",
        "eDiscovery",
        "메일 보관 및 보존",
      ],
    },
    {
      name: "Enterprise",
      price: "가격 문의",
      priceDesc: "맞춤형 계약",
      storage: "무제한",
      meet: "500명",
      security: "최고급",
      support: "프리미엄",
      features: [
        "Business Plus의 모든 기능",
        "S/MIME 암호화",
        "고급 DLP",
        "엔터프라이즈급 관리 콘솔",
        "고급 엔드포인트 관리",
        "24/7 프리미엄 지원",
      ],
    },
  ];

  // 공식 파트너 혜택
  const partnerBenefits = [
    {
      icon: <FaHandshake />,
      title: "특별 할인",
      description: "공식 파트너사를 통한 구매 시 일반 구매 대비 최대 10% 할인",
    },
    {
      icon: <FaGlobeAsia />,
      title: "확장된 무료 체험",
      description: "일반 14일에서 30일로 연장된 무료 체험 기간 제공",
    },
    {
      icon: <FaTools />,
      title: "전문 기술 지원",
      description: "구글 인증 전문가의 직접적인 기술 지원 및 구축 컨설팅",
    },
    {
      icon: <FaShieldAlt />,
      title: "안전한 전환",
      description:
        "기존 시스템에서 Google Workspace로의 안전한 마이그레이션 지원",
    },
  ];

  return (
    <div className="bg-white overflow-hidden">
      {/* 히어로 섹션 */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white overflow-hidden">
        {/* 배경 애니메이션 효과 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -inset-[10px] opacity-50">
            <svg width="100%" height="100%">
              <defs>
                <pattern
                  id="dotPattern"
                  x="0"
                  y="0"
                  width="40"
                  height="40"
                  patternUnits="userSpaceOnUse"
                >
                  <circle cx="20" cy="20" r="1" fill="rgba(255,255,255,0.4)" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dotPattern)" />
            </svg>
          </div>
        </div>

        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-12 md:mb-0" data-aos="fade-right">
              <div className="inline-flex items-center bg-blue-800/50 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-blue-700/50">
                <img
                  src="https://www.gstatic.com/partners/badge/images/2022/PartnerBadgeClickable.svg"
                  alt="Google Partner"
                  className="h-8 mr-3"
                />
                <span className="text-sm font-medium">구글 공식 파트너</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                <span className="block text-white">Work smarter with</span>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-white to-blue-300">
                  Google Workspace
                </span>
              </h1>

              <p className="text-lg md:text-xl text-blue-100 mb-8 max-w-lg">
                이메일부터 화상회의, 문서 작업까지 - 하나의 통합 솔루션으로
                비즈니스의 혁신을 경험하세요.
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="http://pf.kakao.com/_jyebn/chat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <button className="bg-white text-blue-900 font-medium px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors duration-300 shadow-lg flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M3 3h18v18H3V3z" fill="#4285F4" />
                      <path d="M3 3h9v9H3V3z" fill="#EA4335" />
                      <path d="M12 3h9v9h-9V3z" fill="#FBBC05" />
                      <path d="M3 12h9v9H3v-9z" fill="#34A853" />
                      <path d="M12 12h9v9h-9v-9z" fill="#4285F4" />
                    </svg>
                    30일 무료 체험 시작하기
                  </button>
                </a>
                <a
                  href="http://pf.kakao.com/_jyebn/chat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <button className="bg-yellow-400 text-black font-medium px-6 py-3 rounded-lg hover:bg-yellow-300 transition-colors duration-300 shadow-lg flex items-center">
                    <img
                      src="https://img.icons8.com/color/24/000000/kakaotalk.png"
                      alt="카카오톡"
                      className="w-5 h-5 mr-2"
                    />
                    전문가 상담하기
                  </button>
                </a>
              </div>
            </div>

            <div
              className="md:w-1/2 md:pl-12"
              data-aos="fade-left"
              data-aos-delay="200"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-purple-500/20 rounded-2xl blur-2xl"></div>
                <img
                  src="https://img.freepik.com/free-vector/flat-design-cms-concept-illustrated_23-2148796510.jpg"
                  alt="Google Workspace"
                  className="relative rounded-xl shadow-2xl w-full max-w-lg mx-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* 물결 모양 디바이더 */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 120"
            fill="#ffffff"
          >
            <path d="M0,96L48,85.3C96,75,192,53,288,53.3C384,53,480,75,576,80C672,85,768,75,864,64C960,53,1056,43,1152,48C1248,53,1344,75,1392,85.3L1440,96L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* 핵심 가치 섹션 */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              비즈니스 성장을 위한 핵심 가치
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Google Workspace는 단순한 생산성 도구가 아닌, 비즈니스 혁신을 위한
              통합 플랫폼입니다.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-8 transform transition duration-300 hover:-translate-y-2 hover:shadow-xl"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-6 text-2xl">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 앱 쇼케이스 섹션 */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              올인원 비즈니스 솔루션
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Google Workspace의 강력한 앱으로 팀 생산성을 높이고 협업을
              강화하세요.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {apps.map((app, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transition duration-300 hover:shadow-lg"
                data-aos="zoom-in"
                data-aos-delay={index * 50}
              >
                <div className={`h-2 bg-${app.color}-500`}></div>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className={`text-${app.color}-500 text-2xl mr-3`}>
                      {app.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {app.title}
                    </h3>
                  </div>
                  <p className="text-gray-700 mb-4">{app.description}</p>
                  <ul className="space-y-2">
                    {app.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <span
                          className={`text-${app.color}-500 mr-2 mt-1 flex-shrink-0`}
                        >
                          <FaCheckCircle size={14} />
                        </span>
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 요금제 비교 섹션 */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              귀사에 맞는 요금제 선택
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              비즈니스 규모와 요구사항에 따라 적합한 플랜을 선택하세요.
            </p>
          </div>

          {/* 그리드 레이아웃으로 변경 */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-lg border overflow-hidden transition duration-300 h-full ${
                  plan.isPopular
                    ? "border-blue-500 relative md:transform md:scale-105 z-10"
                    : "border-gray-200"
                }`}
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                {plan.isPopular && (
                  <div className="bg-blue-500 text-white py-2 px-4 text-center text-sm font-medium">
                    인기 요금제
                  </div>
                )}

                <div className="p-5 md:p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline mb-5">
                    <span className="text-2xl md:text-3xl font-bold text-blue-600">
                      {plan.price}
                    </span>
                    <span className="text-gray-500 ml-2 text-sm">
                      {plan.priceDesc}
                    </span>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-gray-600 text-sm">스토리지</span>
                      <span className="font-medium text-sm">
                        {plan.storage}
                      </span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-gray-600 text-sm">화상회의</span>
                      <span className="font-medium text-sm">
                        최대 {plan.meet}명
                      </span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-gray-600 text-sm">보안 기능</span>
                      <span className="font-medium text-sm">
                        {plan.security}
                      </span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-gray-600 text-sm">기술 지원</span>
                      <span className="font-medium text-sm">
                        {plan.support}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 mb-6">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start">
                        <FaCheckCircle
                          className="text-green-500 mt-0.5 mr-2 flex-shrink-0"
                          size={12}
                        />
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <a
                    href="http://pf.kakao.com/_jyebn/chat"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full mt-auto"
                  >
                    <button
                      className={`w-full py-2.5 rounded-lg font-medium transition-colors text-sm ${
                        plan.isPopular
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                      }`}
                    >
                      견적 문의하기
                    </button>
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-500 text-sm mt-8">
            * 연간 계약 시 추가 할인 혜택을 제공합니다. 자세한 내용은 상담을
            통해 확인하세요.
          </p>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%">
            <pattern
              id="circles"
              x="0"
              y="0"
              width="60"
              height="60"
              patternUnits="userSpaceOnUse"
            >
              <circle
                cx="30"
                cy="30"
                r="20"
                fill="none"
                stroke="white"
                strokeWidth="0.5"
              />
            </pattern>
            <rect width="100%" height="100%" fill="url(#circles)" />
          </svg>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="w-full mx-auto" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Google Workspace로 비즈니스 혁신을 시작하세요
            </h2>
            <p className="text-xl text-white mb-8">
              지금 바로 30일 무료 체험을 통해 Google Workspace의 강력한 기능을
              경험해보세요.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="http://pf.kakao.com/_jyebn/chat"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <button className="w-full sm:w-auto bg-white text-blue-700 font-semibold px-8 py-4 rounded-lg hover:bg-blue-50 transition-colors duration-300 shadow-lg">
                  30일 무료로 시작하기
                </button>
              </a>
              <a
                href="http://pf.kakao.com/_jyebn/chat"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <button className="w-full sm:w-auto bg-transparent text-white font-semibold px-8 py-4 rounded-lg border-2 border-white hover:bg-white/10 transition-colors duration-300">
                  상담 문의하기
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Overview;
